// Signup email verification landing page.
//
// The confirmation email links here as ``/verify-email?token=<jwt>``
// (scope=``email-verify``, 24h TTL). On mount the token is POSTed to
// ``/api/auth/verify-email``, which flips ``email_verified`` and issues
// the account's first access token — so a successful click signs the
// user straight in (via the same ``applySsoSession`` helper the SSO
// callback uses) and lands on the dashboard.
//
// Failure modes (expired link, tampered token, blocked account) render
// an explanation with a way back to /auth, where the sign-in form offers
// a resend button for unverified accounts.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { CheckCircle2, Loader2, MailX } from 'lucide-react';
import { BACKEND_URL } from '../runtimeConfig';

const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const { applySsoSession } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError(t('The verification link is incomplete — open the link from the email again.'));
      return;
    }

    axios
      .post(`${API}/auth/verify-email`, { token })
      .then((res) => {
        const { access_token, user } = res.data || {};
        setStatus('success');
        if (access_token) {
          applySsoSession(access_token, user);
        }
        toast.success(t('Email confirmed — welcome aboard!'));
        setTimeout(() => navigate('/dashboard', { replace: true }), 900);
      })
      .catch((e) => {
        setStatus('error');
        setError(
          e.response?.data?.detail || t('Verification failed — the link may have expired.')
        );
      });
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4"
      data-testid="verify-email-page"
    >
      <div className="max-w-md w-full text-center space-y-4">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
            <h1 className="text-xl font-display">{t('Confirming your email…')}</h1>
            <p className="text-sm text-muted-foreground">{t('This takes a second.')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-primary" />
            <h1 className="text-xl font-display">{t('Email confirmed — welcome aboard!')}</h1>
            <p className="text-sm text-muted-foreground">{t('Taking you to your dashboard…')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <MailX className="w-10 h-10 mx-auto text-destructive" />
            <h1 className="text-xl font-display">{t('Verification failed')}</h1>
            <p className="text-sm text-muted-foreground" data-testid="verify-email-error">
              {error}
            </p>
            <Button onClick={() => navigate('/auth')} data-testid="verify-email-back">
              {t('Back to sign in')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
